<template>
  <new-modal-dialog
    title="Merge"
    :fullscreen="$vuetify.breakpoint.xsOnly"
    max-width="80vw"
    persistent
    scrollable
    @close="emit('close')"
  >
    <v-data-table
      v-model="selectedServices"
      v-reorder-table
      show-select
      :headers="headers"
      :items="services"
      disable-pagination
      disable-sort
      mobile-breakpoint="0"
      hide-default-footer
    >
      <template #item.imageUrl="{item}">
        <v-img
          class="px-0 mx-0"
          contain
          max-width="50"
          max-height="38"
          :src="item.imageUrl"
        />
      </template>

      <template #item.color="{item}">
        <v-icon :color="item.color">
          mdi-circle
        </v-icon>
      </template>

      <template #item.title="{item}">
        <v-tooltip
          bottom
          content-class="desc-content-tooltip black"
        >
          <template #activator="{ on, attrs }">
            <span
              v-bind="attrs"
              v-on="on"
            >{{ item.title }}</span>
          </template>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <span v-html="item.description" />
        </v-tooltip>
      </template>

      <template #item.minPartySize="{item}">
        {{ item.minPartySize }} - {{ item.maxPartySize }}
      </template>

      <template #item.duration="{item}">
        {{ item.duration === undefined ? '' : localized.durationFromSeconds(item.duration * 60) }}
      </template>

      <template #item.localStartDate="{item}">
        {{ item.localStartTime }}
      </template>

      <template #item.localEndDate="{item}">
        {{ item.localEndTime }}
      </template>

      <template #item.locale="{item}">
        {{ item.locale }}
      </template>

      <template #item.weekDays="{item}">
        <span
          v-for="w, i in localized.weekdays()"
          :key="i"
        >
          <v-chip
            class="white--text px-1 mx-0"
            label
            :color="item.weekDays?.includes(w.value === 0 ? 7 : w.value ) ? 'green' : 'grey'"
            filled
            x-small
          >{{ w.text }}</v-chip>
        </span>
      </template>

      <template #item.isActive="{item}">
        <v-chip
          :color="colorForServiceStatus(item)"
          small
        >
          {{ labelForServiceStatus(item) }}
        </v-chip>
      </template>
    </v-data-table>
    <template #actions>
      <v-spacer />
      <v-btn
        :disabled="selectedServices.length === 0"
        class="black--text warning"
        @click="mergeServices"
      >
        <v-icon>mdi-merge</v-icon>
        Merge
      </v-btn>
      <v-btn
        text
        color="error"
        @click="$emit('close')"
      >
        {{ $t('button.close') }}
      </v-btn>
    </template>
  </new-modal-dialog>
</template>

<script setup lang="ts">
import NewModalDialog from '@/components/dialogs/NewModalDialog.vue';
import {
  PropType, computed, getCurrentInstance, onMounted, ref,
} from 'vue';
import CardNumberField from '@/components/forms/CardNumberField.vue';
import Giftcard from '@/grpc-api/model/giftcard';
import { useLocalized, useVuetify } from '@/components/helpers/vue2-helpers';
import i18n from '@/plugins/i18n';
import store from '@/store/store';
import ReservationService from '@/grpc-api/model/reservation-service';
import { performDispatchAction } from '@/services/vue-utils';
import { loadStoreModule } from '@/services/module-loader';
import ServiceManagerStore from '@/store/service-manager-store';
import toast from '@/plugins/toast-plugin/toast';

const emit = defineEmits(['close']);
const vuetify = useVuetify();
const parentService = ref(undefined as ReservationService | undefined);
const services = ref([] as ReservationService[]);

const localized = useLocalized();

const props = defineProps<{parentServiceId: number}>();

const canBeMerged = (s: ReservationService) => {
  const p = parentService.value;
  if (!p || p.translations.length === 0) return false;

  if (s.translations?.length > 1) return false; // already more translations
  if (s.id === p.id) return false; // same service
  if (!s.locale && s.translations?.length === 0) return false; // no locale to add translation

  const sls = s.translations.length === 0 ? [s.locale] : s.translations.map((t) => t.locale);
  if (p.translations.some((t) => sls.includes(t.locale))) return false; // overlapping locales

  return true;
};

const reset = async () => {
  const entities = store.getters.serviceManagertServices as ReservationService[];
  parentService.value = entities.find((s) => s.id === props.parentServiceId);
  services.value = entities.filter((s) => canBeMerged(s));
};

const load = async () => {
  if (!store.getters.isServiceManagerLoaded) {
    await performDispatchAction('loadServiceManager', {}, { streamLoad: true }) as ReservationService[] | null;
  }
  reset();
};

onMounted(() => {
  loadStoreModule(getCurrentInstance()?.proxy!, 'serviceManager', ServiceManagerStore, async () => { load(); });
});

const headers = computed(
  () => [
    {
      text: 'Id', width: '32', value: 'id', align: 'center',
    },
    {
      text: i18n.t('label.service_manager_image'),
      width: '50',
      value: 'imageUrl',
      align: 'center',
    },
    {
      text: i18n.t('label.color'), width: '24', value: 'color', align: 'center', cellClass: 'mx-1',
    },
    {
      text: 'Service Code', width: '24', value: 'code', align: 'center', cellClass: 'mx-1',
    },
    {
      text: i18n.t('label.service_title'), value: 'title', align: 'start', width: '100%',
    },
    ...vuetify.breakpoint.lgAndUp
      ? [{
        text: i18n.t('label.weekdays'), value: 'weekDays', align: 'start', cellClass: 'dt-cell-stretch', width: '100px',
      },
      {
        text: i18n.t('label.language'), value: 'locale', align: 'start', cellClass: 'dt-cell-stretch',
      },
      ] : [],
    ...vuetify.breakpoint.mdAndUp
      ? [{
        text: i18n.t('label.service_time_from'), value: 'localStartDate', align: 'center', width: '32',
      },
      {
        text: i18n.t('label.service_time_until'), value: 'localEndDate', align: 'center', width: '32',
      }] : [],
    {
      text: i18n.t('label.active'), width: '32', value: 'isActive', align: 'start', sortable: true,
    },
    { value: 'actions', width: '32' },
  ],
);

const colorForServiceStatus = (s: ReservationService) => {
  if (!s.isActive) return 'gray';
  if (s.tabs.length > 0 && s.tabs.every((t) => !t.usingWeekdaysAndTimes)) { return 'red'; }
  return 'success';
};
const labelForServiceStatus = (s: ReservationService) => {
  if (!s.isActive) return i18n.t('label.inactive');
  if (s.tabs.length > 0 && s.tabs.every((t) => !t.usingWeekdaysAndTimes)) { return i18n.t('label.offline'); }
  return i18n.t('label.active');
};

const selectedServices = ref([] as ReservationService[]);

const mergeServices = async () => {
  const langages = new Set<string>();
  let canMerge = true;
  selectedServices.value.forEach((s) => {
    if (!s.locale) {
      toast.error('Service has no language set.'); canMerge = false; return;
    }
    if (langages.has(s.locale)) {
      toast.error(`Cannot merge services with the same language: ${s.locale}`); canMerge = false; return;
    }
    langages.add(s.locale);
  });
  if (!canMerge) { return; }

  // eslint-disable-next-line no-alert
  const confirmed = window.confirm('Do You want to merge the selected services?');
  if (!confirmed) { return; }
  console.log(selectedServices);

  const conflicts = await performDispatchAction('serviceManagerMergeServices', {
    service: parentService.value, toMergeServices: selectedServices.value,
  }) as string[] | null;
  if (conflicts === null || conflicts.length === 0) {
    emit('close');
    return;
  }

  const conflictMessage = `Conflicts found:\n- ${conflicts.join('\n- ')}.`;
  // eslint-disable-next-line no-alert
  alert(conflictMessage);
};

</script>

<style>

.desc-content-tooltip {
  max-width: 300px;
}

</style>
